import React, { useState } from "react";
import { TListingProfitReportRow } from "../../../../domain/codecs/formIO/ListingsProfitReportFormIO";
import { penceToCopyText } from "../../../../shared/src/util";
import { CRMCardOutside } from "../CRMCardOutside/CRMCardOutside";
import { CRMCardOutsideSharpCorners } from "../CRMCardOutsideSharpCorners/CRMCardOutsideSharpCorners";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { Background } from "../BuildingBlocks/Background";
import { Text } from "../BuildingBlocks/Text";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMIcon } from "../CRMIcon/CRMIcon";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";

const COLS = "1fr 190px 110px 110px 120px";

type TSortKey = "address" | "status" | "forecast" | "actual" | "blended";
type TSortDir = "asc" | "desc";

const statusLabel = (status: string): string => {
    switch (status) {
        case "instructed_needs_market_prep": return "Instructed: needs market prep";
        case "instructed_on_the_market": return "Instructed: on the market";
        case "sstc": return "SSTC";
        case "auction": return "At auction";
        case "completed": return "Completed";
        case "lost": return "Lost";
        default: return status;
    }
};

const profitColour = (pence: number) =>
    pence < 0 ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0 : CRMColors.HIGHLIGHTS_POSITIVE_GREEN_0;

const sortValue = (row: TListingProfitReportRow, key: TSortKey): string | number => {
    switch (key) {
        case "address": return row.address.toLowerCase();
        case "status": return row.status;
        case "forecast": return row.total_forecast_net_pence;
        case "actual": return row.total_actual_net_pence;
        case "blended": return row.total_blended_net_pence;
    }
};

const HeaderCell = (props: {
    label: string;
    sortKey: TSortKey;
    activeKey: TSortKey;
    dir: TSortDir;
    onSort: (key: TSortKey) => void;
}): JSX.Element => {
    const isActive = props.activeKey === props.sortKey;
    return (
        <div className="crm-profit-report__header-cell" onClick={() => props.onSort(props.sortKey)}>
            <SpacingRow spacing={CRMSpacing.TINY}>
                <Text color={CRMColors.NEUTRAL_4}>{props.label}</Text>
                <CRMIcon
                    iconName={isActive ? (props.dir === "asc" ? "arrow-up" : "arrow-down") : "inward-arrows"}
                    colour="neutral-4"
                    size="tiny"
                />
            </SpacingRow>
        </div>
    );
};

const Row = (props: { row: TListingProfitReportRow }): JSX.Element => (
    <a
        className="crm-profit-report__row-link"
        href={`/crm/listing/${props.row.listing_id}?visible_tab=pnl`}
        target="_blank"
        rel="noreferrer"
    >
        <SpacingRow spacing={CRMSpacing.MEDIUM} childSize={COLS}>
            <Text>{props.row.address}</Text>
            <Text color={CRMColors.NEUTRAL_4}>{statusLabel(props.row.status)}</Text>
            <Text align="right">{penceToCopyText(props.row.total_forecast_net_pence)}</Text>
            <Text align="right">{penceToCopyText(props.row.total_actual_net_pence)}</Text>
            <Text align="right">
                <WeightBold>
                    <Text color={profitColour(props.row.total_blended_net_pence)}>
                        {penceToCopyText(props.row.total_blended_net_pence)}
                    </Text>
                </WeightBold>
            </Text>
        </SpacingRow>
    </a>
);

const TotalCell = (props: { pence: number; colour?: boolean }): JSX.Element => (
    <Text align="right">
        <WeightBold>
            <Text color={props.colour ? profitColour(props.pence) : undefined}>{penceToCopyText(props.pence)}</Text>
        </WeightBold>
    </Text>
);

export const CRMListingsProfitReportTable = (props: { rows: Array<TListingProfitReportRow> }): JSX.Element => {
    const [sortKey, setSortKey] = useState<TSortKey>("blended");
    const [sortDir, setSortDir] = useState<TSortDir>("asc");

    const onSort = (key: TSortKey): void => {
        if (key === sortKey) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const sortedRows = [...props.rows].sort((a, b) => {
        const va = sortValue(a, sortKey);
        const vb = sortValue(b, sortKey);
        const cmp = va < vb ? -1 : va > vb ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
    });

    const totals = props.rows.reduce(
        (acc, r) => ({
            forecast: acc.forecast + r.total_forecast_net_pence,
            actual: acc.actual + r.total_actual_net_pence,
            blended: acc.blended + r.total_blended_net_pence,
        }),
        { forecast: 0, actual: 0, blended: 0 },
    );

    if (props.rows.length === 0) {
        return <Text color={CRMColors.NEUTRAL_4}>No instructed listings to report on yet.</Text>;
    }

    return (
        <SpacingColumn spacing={CRMSpacing.SMALL}>
            {/* HEADER */}
            <CRMCardOutside borderRounding="none">
                <div className="crm-profit-report__header-row">
                    <SpacingRow spacing={CRMSpacing.MEDIUM} childSize={COLS}>
                        <HeaderCell label="Property" sortKey="address" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                        <HeaderCell label="Status" sortKey="status" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                        <HeaderCell label="Forecast" sortKey="forecast" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                        <HeaderCell label="Actual" sortKey="actual" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                        <HeaderCell label="Blended" sortKey="blended" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                    </SpacingRow>
                </div>
            </CRMCardOutside>

            {/* ROWS */}
            <CRMCardOutsideSharpCorners shadow={true}>
                <SpacingColumn spacing={CRMSpacing.TINY}>
                    {sortedRows.map((row) => <Row key={row.listing_id} row={row} />)}
                </SpacingColumn>
            </CRMCardOutsideSharpCorners>

            {/* TOTALS */}
            <CRMCardOutsideSharpCorners>
                <Background padding={`${CRMSpacing.MEDIUM} ${CRMSpacing.MEDIUM}`} color={CRMColors.NEUTRAL_12}>
                    <SpacingRow spacing={CRMSpacing.MEDIUM} childSize={COLS}>
                        <WeightBold><Text>Company total ({props.rows.length})</Text></WeightBold>
                        <Text> </Text>
                        <TotalCell pence={totals.forecast} />
                        <TotalCell pence={totals.actual} />
                        <TotalCell pence={totals.blended} colour={true} />
                    </SpacingRow>
                </Background>
            </CRMCardOutsideSharpCorners>
        </SpacingColumn>
    );
};
